
import AppButton from '@/components/buttons/AppButton.vue';
import IconCalendar from '@/components/icons/IconCalendar.vue';
import IconDuration from '@/components/icons/IconDuration.vue';
import IconParticipants from '@/components/icons/IconParticipants.vue';
import IconChevronUp from '@/components/icons/IconChevronUp.vue';
import BaseButton from '@/components/buttons/baseButton.vue';
import DoctorImage from '@/components/doctor/doctorImage.vue';
import { resolvePriceLabel } from '@/helpers/assetHelper';
import { DateHelper, extractDatePart } from '@/helpers/dateHelper';
import { buildDoctorProfileRoute } from '@/helpers/doctorRouteHelper';

export default {
  name: 'ClassCard',
  components: {
    AppButton,
    BaseButton,
    IconCalendar,
    IconDuration,
    IconParticipants,
    IconChevronUp,
    DoctorImage
  },
  props: {
    classData: {
      type: Object,
      required: true
    },
    showCollapse: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isCollapsed: false
    };
  },
  computed: {
    displayTitle() {
      const description = this.classData?.description;
      const extended = this.classData?.extendedDescription;
      return description || extended || this.$t('class');
    },
    toggleAriaLabel() {
      const title = this.displayTitle;
      return this.isCollapsed
        ? this.$t('Expand class', { title })
        : this.$t('Collapse class', { title });
    },
    cardRoute() {
      const params = {};
      const tenantId = this.classData?.tenantId ?? this.$route?.params?.tenantId;
      if (tenantId !== undefined && tenantId !== null && tenantId !== '') {
        params.tenantId = String(tenantId);
      }

      const therapistCandidate = this.classData?.therapist?.id
        ?? this.classData?.bookings?.[0]?.resourceId
        ?? this.$route?.params?.therapistId;
      if (therapistCandidate !== undefined && therapistCandidate !== null && therapistCandidate !== '') {
        params.therapistId = String(therapistCandidate);
      }

      const slotIso = this.classData?.bookings?.[0]?.start
        || this.$route?.query?.dateFrom
        || this.classData?.start;
      const normalizedDate = this.normalizeDateParam(slotIso)
        || this.normalizeDateParam(this.$route?.params?.date)
        || this.normalizeDateParam(DateHelper.toSystemString(new Date()) || '');

      if (normalizedDate) {
        params.date = normalizedDate;
      }

      const route = {
        name: 'ClassDetail',
        params,
      };

      const query = {};
      if (slotIso) {
        const date = new Date(slotIso);
        if (!Number.isNaN(date.getTime())) {
          query.dateFrom = DateHelper.toSystemString(date);
        }
      }

      if (this.classData?.therapy) {
        query.therapy = this.classData.therapy;
      }
      if (this.classData?.therapyType) {
        query.therapyType = this.classData.therapyType;
      }

      if (Object.keys(query).length) {
        route.query = query;
      }

      if (this.classData?.tenantId) {
        try {
          const serialized = JSON.parse(JSON.stringify(this.classData));
          route.state = { classData: serialized };
        } catch (error) {
          void error;
        }
      }

      return route;
    },
    therapistImage() {
      const raw = this.classData?.therapist?.avatarUrl || '';
      return typeof raw === 'string' ? raw.trim() : '';
    },
    therapistProfileRoute() {
      if (!this.classData?.therapist) {
        return null;
      }

      const doctor = {
        ...this.classData.therapist,
        tenantId: this.classData.therapist.tenantId
          ?? this.classData.tenantId
          ?? this.$route?.params?.tenantId
          ?? null,
        tenantIgniteId: this.classData.therapist.tenantIgniteId
          ?? this.classData.tenantIgniteId
          ?? this.classData.tenant?.igniteId
          ?? this.$route?.query?.tenantIgniteId
          ?? null,
        defaultTherapy: this.classData.therapist.defaultTherapy
          ?? this.classData.therapy
          ?? this.$route?.query?.therapy
          ?? null,
        defaultTherapyType: this.classData.therapist.defaultTherapyType
          ?? this.classData.therapyType
          ?? this.$route?.query?.therapyType
          ?? null,
      };

      return buildDoctorProfileRoute(doctor);
    },
    startDisplayParts() {
      const raw = this.classData?.startLocalDisplay;
      if (!raw || typeof raw !== 'string') {
        return { date: '', time: '' };
      }
      const match = raw.match(/^(.*)[T\s]+(\d{1,2}:\d{2}(?::\d{2})?(?:\s*[APMapm.\s]+)?)$/);
      if (!match) {
        return { date: raw, time: '' };
      }
      return { date: match[1].trim(), time: match[2].trim() };
    },
    dateDisplay() {
      const value = this.startDisplayParts.date;
      return value || '--';
    },
    timeDisplay() {
      const value = this.startDisplayParts.time;
      return value || '--';
    },
    calendarDisplay() {
      return this.dateDisplay || this.timeDisplay;
    },
    showTimeRow() {
      return Boolean(this.dateDisplay);
    },
    durationDisplay() {
      if (!this.classData?.durationMinutes) {
        return '--';
      }
      return this.$t('Duration minutes short', { minutes: this.classData.durationMinutes });
    },
    participantsCounts() {
      const normalizeCount = (value) => {
        if (value === null || value === undefined) {
          return null;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) {
          return null;
        }
        return Math.round(numeric);
      };

      const firstValid = (candidates, fallback = null) => {
        if (!Array.isArray(candidates)) {
          return fallback;
        }
        for (const candidate of candidates) {
          const normalized = normalizeCount(candidate);
          if (normalized !== null) {
            return normalized;
          }
        }
        return fallback;
      };

      const slots = Array.isArray(this.classData?.bookings) ? this.classData.bookings : [];

      let current = normalizeCount(this.classData?.participantsIn);
      if (current === null) {
        const slotCandidateValues = [];
        slots.forEach((slot) => {
          slotCandidateValues.push(
            slot?.participantsIn,
            slot?.participants,
            slot?.booked,
            slot?.bookedParticipants,
            slot?.occupied,
            slot?.enrolled,
            slot?.currentParticipants,
          );
        });
        current = firstValid(slotCandidateValues, 0);
      }
      if (current === null) {
        current = 0;
      }

      let total = normalizeCount(this.classData?.maxParticipants);

      const resolveTotalFromSlot = (slot, assumedCurrent = current) => {
        const totals = firstValid([
          slot?.maxParticipants,
          slot?.capacity,
          slot?.maxCapacity,
          slot?.maximumCapacity,
          slot?.capacityMax,
        ]);
        if (totals !== null) {
          return totals;
        }

        const remaining = firstValid([
          slot?.availableSpots,
          slot?.available,
          slot?.remaining,
          slot?.remainingCapacity,
          slot?.capacityLeft,
        ]);

        if (remaining !== null) {
          const slotCurrent = firstValid([
            slot?.participantsIn,
            slot?.participants,
            slot?.booked,
            slot?.bookedParticipants,
            slot?.occupied,
            slot?.enrolled,
            slot?.currentParticipants,
            assumedCurrent,
          ]);
          if (slotCurrent !== null) {
            return slotCurrent + remaining;
          }
        }
        return null;
      };

      if (total === null) {
        for (const slot of slots) {
          const resolvedTotal = resolveTotalFromSlot(slot);
          if (resolvedTotal !== null) {
            total = resolvedTotal;
            break;
          }
        }
      }

      if (total !== null && total < current) {
        total = current;
      }

      return {
        current,
        total,
      };
    },
    participantsDisplay() {
      const { current, total } = this.participantsCounts;
      if (total !== null) {
        return `${current}/${total}`;
      }
      return `${current}`;
    },
    availabilityLabel() {
      const { current, total } = this.participantsCounts;
      if (total !== null && total > 0) {
        return current >= total
          ? this.$t('Full')
          : this.$t('Available');
      }
      return this.$t('Available');
    },
    availabilityStatusClass() {
      const { current, total } = this.participantsCounts;
      if (total !== null && total > 0 && current >= total) {
        return 'class-card__badge--full';
      }
      return 'class-card__badge--available';
    },
    centerAddress() {
      const center = this.classData?.center;
      if (center) {
        const address = typeof center.address === 'string' ? center.address.trim() : '';
        if (address) {
          return address;
        }
        if (typeof center.name === 'string' && center.name.trim()) {
          return center.name.trim();
        }
      }

      const fullAddress = typeof this.classData?.fullAddress === 'string'
        ? this.classData.fullAddress.trim()
        : '';
      if (fullAddress) {
        return fullAddress;
      }

      const locationName = typeof this.classData?.locationName === 'string'
        ? this.classData.locationName.trim()
        : '';
      return locationName || null;
    },
    priceDisplay() {
      const priceObject = this.classData.price ?? null;
      const fallback = this.classData.priceLabel ?? null;
      const resolved = resolvePriceLabel(priceObject, fallback);
      return resolved || '--';
    }
  },
  methods: {
    toggleCollapse() {
      if (!this.showCollapse) {
        return;
      }
      this.isCollapsed = !this.isCollapsed;
    },
    normalizeDateParam(value) {
      if (!value || typeof value !== 'string') {
        return null;
      }
      const candidate = extractDatePart(value) || value;
      if (!candidate) {
        return null;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
        return candidate;
      }
      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return null;
    },
    navigateToClass() {
      const route = { ...this.cardRoute };
      if (!route.params?.tenantId || !route.params?.therapistId || !route.params?.date) {
        return;
      }
      this.$router.push(route).catch((error) => {
        void error;
      });
    }
  }
};
